/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0 Authorization Code Flow.
 *
 * Generates cryptographically secure code verifiers and SHA-256 code challenges
 * using the Web Crypto API.
 */

/**
 * Base64url-encode a Uint8Array (RFC 7636 Appendix A).
 */
function base64urlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Generate a cryptographically secure code verifier (128 random bytes, base64url-encoded).
 */
export async function generateCodeVerifier(): Promise<string> {
  const buffer = new Uint8Array(128);
  crypto.getRandomValues(buffer);
  return base64urlEncode(buffer);
}

/**
 * Generate a cryptographically secure OAuth state parameter (32 random bytes, base64url-encoded).
 * Used for CSRF protection per RFC 6749 §10.12.
 */
export function generateState(): string {
  const buffer = new Uint8Array(32);
  crypto.getRandomValues(buffer);
  return base64urlEncode(buffer);
}

/**
 * Generate a SHA-256 code challenge from a code verifier (S256 method).
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64urlEncode(new Uint8Array(digest));
}
